/**
 * A smart-prompt review topic.
 *
 * - `display` is the short label shown to the user under the review textarea.
 * - `matchHint` is sent to the LLM (Gemini) alongside the display label so the
 *   model knows precisely what counts as a semantic match. It is never shown
 *   to the user.
 */
export type ReviewTopic = {
  display: string;
  matchHint: string;
};

const SCENT_HINT =
  "Matches text describing how the product smells or what it smells like (smells fresh, lightly scented, strong fragrance, smells like coconut, no smell). Does NOT match how it feels on skin/hair or its results.";

const FEEL_ON_SKIN_HINT =
  "Matches text describing the sensory experience on the skin after application (soft, sticky, hydrated, tight, greasy, smooth, plumped, light). Does NOT match visible results, scent, or the application process.";

const RESULTS_HINT =
  "Matches text describing observed changes or outcomes from using the product over time (clearer skin, fewer breakouts, brighter complexion, no improvement, hair feels stronger after a week). Does NOT match immediate sensory feel, scent, or application experience.";

const APPLICATION_HINT =
  "Matches text describing the experience of applying the product — how easy, smooth, blendable, messy, or quick the application is (glides on, hard to spread, blends easily, leaves streaks). Does NOT match how it feels on skin afterwards or visible results.";

export const BEAUTY_TOPICS: Record<string, ReadonlyArray<ReviewTopic>> = {
  fragrance: [
    {
      display: "Fragrance notes",
      matchHint:
        "Matches text describing the overall character or notes of the scent (floral, woody, sweet, citrus, smells of roses, smells musky). Does NOT match emotional reactions, intensity, or how long it lasts.",
    },
    {
      display: "First impression",
      matchHint:
        "Matches text describing the user's initial reaction or how the fragrance made them feel when they first wore it (loved it instantly, felt confident, reminded me of summer, took me back to my childhood). Does NOT match descriptions of the scent's character itself or its longevity.",
    },
    {
      display: "Intensity",
      matchHint:
        "Matches text describing how strong, faint, subtle, or overpowering the scent is at any given moment (very strong, light and subtle, projects across the room, almost imperceptible). Does NOT match how long the scent lasts.",
    },
    {
      display: "Scent longevity",
      matchHint:
        "Matches text describing how long the scent lingers on skin or clothes after applying (lasted all day, faded after an hour, still smell it the next morning). Does NOT match how long the bottle/product itself lasts as a physical volume of liquid, and does NOT match how strong the scent is.",
    },
  ],
  skincare: [
    { display: "Application", matchHint: APPLICATION_HINT },
    { display: "Feel on skin", matchHint: FEEL_ON_SKIN_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
  makeup: [
    {
      display: "Coverage",
      matchHint:
        "Matches text describing how much the product conceals or covers the skin (full coverage, sheer, buildable, hides redness, doesn't hide blemishes). Does NOT match finish, shade match, or wear time.",
    },
    {
      display: "Finish",
      matchHint:
        "Matches text describing the final visual appearance of the product on skin (matte, dewy, glowy, natural, satin, shiny). Does NOT match coverage, shade match, or longevity.",
    },
    {
      display: "Shade match",
      matchHint:
        "Matches text describing how well the product's colour matches the user's skin tone or undertone (perfect match, too dark, too pink, oxidises orange, undertone is right). Does NOT match coverage, finish, or general pigmentation.",
    },
    { display: "Application", matchHint: APPLICATION_HINT },
    {
      display: "Staying power",
      matchHint:
        "Matches text describing how the product looks or holds up over hours of wear, OR how long it lasts before needing reapplication (lasted all day, faded by lunch, didn't budge, still looked fresh after 8 hours, transferred onto my mask). Does NOT match initial application, coverage, or finish.",
    },
  ],
  bodycare: [
    { display: "Scent", matchHint: SCENT_HINT },
    { display: "Feel on skin", matchHint: FEEL_ON_SKIN_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
  haircare: [
    {
      display: "Hair feel",
      matchHint:
        "Matches text describing the sensory state of the hair after use (soft, smooth, frizzy, greasy, light, weighed down, silky, tangled). Does NOT match visible results like shine, scent, or how easy it was to apply.",
    },
    { display: "Scent", matchHint: SCENT_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
};

// Uses the last but one hex digit of the ID to determine whether to hide or show topics,
// with values 0-7 showing topics and 8-f hiding topics
export function hideForABTest(orderId?: string | null): boolean {
  if (!orderId) return false;
  const lastChar = orderId.replace(/-/g, "").slice(-2, -1).toLowerCase();
  return parseInt(lastChar, 16) >= 8;
}

export type SmartPromptsOverride = "on" | "off" | undefined;

export function getReviewTopicsForCampaign(
  showSmartPrompts: boolean,
  category: string,
  subcategory: string,
  orderId?: string,
  override?: SmartPromptsOverride,
): ReadonlyArray<ReviewTopic> {
  if (override === "off") return [];
  if (!showSmartPrompts) return [];
  if (category.toLowerCase() !== "beauty") return [];
  if (override !== "on" && hideForABTest(orderId)) return [];
  return BEAUTY_TOPICS[subcategory.toLowerCase()] ?? [];
}

export type SmartPromptsTrackingPayload =
  | {
      metadata: {
        tests: [
          {
            id: 3;
            name: "smart_prompts_ab_test_static";
            variation: { id: "A"; name: "smart_prompts_static_shown" };
          },
        ];
        smart_prompts: {
          prompts: string[];
          shown_count: number;
          category: string;
          subcategory: string;
        };
      };
    }
  | {
      metadata: {
        tests: [
          {
            id: 3;
            name: "smart_prompts_ab_test_static";
            variation: { id: "B"; name: "smart_prompts_static_control" };
          },
        ];
      };
    };

export function buildSmartPromptsPayload(
  category: string,
  subcategory: string,
  orderId?: string | null,
  override?: SmartPromptsOverride,
): SmartPromptsTrackingPayload | null {
  if (category.toLowerCase() !== "beauty") return null;
  if (subcategory.toLowerCase() === "other") return null;

  const topics = getReviewTopicsForCampaign(
    true, // not in preview mode when we are building the payload
    category,
    subcategory,
    orderId ?? undefined,
    override,
  );
  const shown = topics.length > 0;

  if (shown) {
    return {
      metadata: {
        tests: [
          {
            id: 3,
            name: "smart_prompts_ab_test_static",
            variation: { id: "A", name: "smart_prompts_static_shown" },
          },
        ],
        smart_prompts: {
          prompts: topics.map((t) => t.display),
          shown_count: topics.length,
          category,
          subcategory,
        },
      },
    };
  }

  return {
    metadata: {
      tests: [
        {
          id: 3,
          name: "smart_prompts_ab_test_static",
          variation: { id: "B", name: "smart_prompts_static_control" },
        },
      ],
    },
  };
}
